import { Button, Card, Form, Input, Select, Space, Table, Tag } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type Row = {
  id: string;
  accommodation_no: string;
  category: string;
  summary: string;
  effective_from: string;
  effective_to?: string | null;
  status: string;
  admission_no?: string;
  first_name?: string;
  last_name?: string;
};

export default function InclusionAccommodationListPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.inclusion.manage') || user?.permissions?.includes('education.admin')
  );
  const [status, setStatus] = useState<string | undefined>();
  const [category, setCategory] = useState<string | undefined>();
  const [q, setQ] = useState('');

  const list = useQuery({
    queryKey: ['education-inclusion-accommodations', status, category, q],
    queryFn: async () => {
      const res = await api.get<Row[]>('/education/inclusion/accommodations', {
        params: { status, category, q: q || undefined },
      });
      return Array.isArray(res.data) ? res.data : [];
    },
  });

  return (
    <EducationPageChrome
      title={t('inclusionAccommodations')}
      description={t('inclusionAccommodationsIntro')}
      extra={
        <Space>
          <Button onClick={() => navigate('/education/inclusion')}>{t('inclusion')}</Button>
          {canManage ? (
            <Button type="primary" onClick={() => navigate('/education/inclusion/accommodations/new')}>
              {t('newAccommodation')}
            </Button>
          ) : null}
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card size="small">
          <Form layout="inline" onFinish={() => list.refetch()}>
            <Form.Item label={t('status')}>
              <Select
                allowClear
                style={{ minWidth: 140 }}
                value={status}
                onChange={(v) => setStatus(v || undefined)}
                options={[
                  { value: 'active', label: t('statusActive') },
                  { value: 'ended', label: t('statusEnded') },
                  { value: 'cancelled', label: t('statusCancelled') },
                ]}
              />
            </Form.Item>
            <Form.Item label={t('category')}>
              <Select
                allowClear
                style={{ minWidth: 180 }}
                value={category}
                onChange={(v) => setCategory(v || undefined)}
                options={[
                  { value: 'extra_time', label: t('accCategoryExtraTime') },
                  { value: 'seating', label: t('accCategorySeating') },
                  { value: 'scribe', label: t('accCategoryScribe') },
                  { value: 'reader', label: t('accCategoryReader') },
                  { value: 'assistive_tech', label: t('accCategoryAssistive') },
                  { value: 'break_schedule', label: t('accCategoryBreak') },
                  { value: 'curriculum_adjustment', label: t('accCategoryCurriculum') },
                  { value: 'other', label: t('accCategoryOther') },
                ]}
              />
            </Form.Item>
            <Form.Item label={t('search')}>
              <Input value={q} onChange={(e) => setQ(e.target.value)} allowClear style={{ width: 180 }} />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={list.isFetching}>
              {t('filter')}
            </Button>
          </Form>
        </Card>
        <Card size="small">
          <Table
            rowKey="id"
            size="small"
            loading={list.isLoading}
            dataSource={list.data ?? []}
            pagination={{ pageSize: 25 }}
            onRow={(row) => ({
              onClick: () => navigate(`/education/inclusion/accommodations/${row.id}`),
              style: { cursor: 'pointer' },
            })}
            columns={[
              { title: t('accommodationNo'), dataIndex: 'accommodation_no', width: 150 },
              {
                title: t('student'),
                render: (_: unknown, row: Row) =>
                  `${row.admission_no || ''} — ${row.first_name || ''} ${row.last_name || ''}`.trim(),
              },
              { title: t('category'), dataIndex: 'category', width: 140 },
              { title: t('effectiveFrom'), dataIndex: 'effective_from', width: 120 },
              { title: t('summary'), dataIndex: 'summary', ellipsis: true },
              {
                title: t('status'),
                dataIndex: 'status',
                width: 110,
                render: (v: string) => <Tag>{v}</Tag>,
              },
            ]}
          />
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
